/**
 * TemplateBlockDelayEditPure
 *
 * Inline editor that swaps in for the wait pill in the template block
 * header. Same chrome as `CampaignDayDelayEditPure` so the two pages
 * read as one design language: a dashed-border form with a number input
 * + Update + Cancel buttons.
 */
import type { ComponentProps } from 'react';
import { useState } from 'react';

import { cn } from '../cn';
import { Button, ButtonSegment, Input } from '../Input2';

type DelayUnit = 'days' | 'weeks';

export type TemplateBlockDelayEditPureProps = Omit<
  ComponentProps<'div'>,
  'children' | 'onSubmit'
> & {
  initialDays: number;
  /** When true, seed the editor in "weeks" mode (calendar-week aligned). */
  initialAlignWeek?: boolean;
  /** `days` is the raw day count (weeks mode passes `value * 7`); `alignWeek` marks it week-aligned. */
  onSubmit: (days: number, alignWeek: boolean) => void | Promise<void>;
  onCancel: () => void;
};

export function TemplateBlockDelayEditPure({
  initialDays,
  initialAlignWeek = false,
  onSubmit,
  onCancel,
  className,
  ...divProps
}: TemplateBlockDelayEditPureProps) {
  const [unit, setUnit] = useState<DelayUnit>(initialAlignWeek ? 'weeks' : 'days');
  const [value, setValue] = useState(`${initialAlignWeek ? Math.max(1, Math.round(initialDays / 7)) : initialDays}`);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const parsed = Number.parseInt(value, 10);
    if (Number.isNaN(parsed) || parsed < (unit === 'weeks' ? 1 : 0)) return;
    void onSubmit(unit === 'weeks' ? parsed * 7 : parsed, unit === 'weeks');
  };

  return (
    <div
      {...divProps}
      data-testid="template-block-delay-edit"
      className={cn('flex w-full', className)}>
      <form
        onSubmit={handleSubmit}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            onCancel();
          }
        }}
        className="inline-flex items-center gap-2 rounded-lg border-2 border-dashed border-lynch-300 bg-lynch-50/60 px-3 py-2 shadow-xs dark:border-slate-600 dark:bg-slate-800/40">
        <span className="text-xs font-medium text-lynch-700 dark:text-lynch-300">wait</span>
        <Input
          data-testid="template-block-delay-edit-input"
          type="number"
          min={unit === 'weeks' ? 1 : 0}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          autoFocus
          size="xs"
          className="w-16"
        />
        <ButtonSegment
          data-testid="template-block-delay-edit-unit"
          size="xs"
          selectedId={unit}
          onChange={(u) => setUnit(u)}>
          <Button id="days" data-testid="template-block-delay-edit-unit-days">days</Button>
          <Button id="weeks" data-testid="template-block-delay-edit-unit-weeks">weeks</Button>
        </ButtonSegment>
        <Button
          data-testid="template-block-delay-edit-submit"
          type="submit"
          size="xs"
          color="blue">
          Update
        </Button>
        <Button
          data-testid="template-block-delay-edit-cancel"
          type="button"
          size="xs"
          color="gray"
          text
          onClick={onCancel}>
          Cancel
        </Button>
      </form>
    </div>
  );
}
